/**
 * Workflow Service
 * Encapsulates business logic for workflow operations
 * Connects to the enhanced backend workflow system (apps/workflows/)
 */
import { workflowAPI, promptsAPI, userAPI } from '../utils/httpClient';

// Module types and metadata (extracted from SimplifiedWorkflow.js)
export const moduleTypes = [
    { id: 'fileInput', name: 'File Input', description: 'Upload source file and set languages' },
    { id: 'termBase', name: 'Term Base', description: 'Optional terminology database' },
    { id: 'memoryBase', name: 'Memory Base', description: 'Optional translation memory' },
    { id: 'translation', name: 'Translation', description: 'Machine translation of text' },
    { id: 'review', name: 'Review', description: 'Review/QA for translation output' },
    { id: 'summary', name: 'Summary', description: 'Summarize or merge multiple outputs' }
];

// Language options
export const languageOptions = [
    {value: 'zh-cn', label: 'Chinese (Simplified)'}, 
    {value: 'zh-tw', label: 'Chinese (Traditional)'},
    {value: 'en-us', label: 'English (US)'}, 
    {value: 'ko', label: 'Korean'}, 
    {value: 'ja', label: 'Japanese'},
    {value: 'th', label: 'Thai'}, 
    {value: 'id', label: 'Indonesian'}, 
    {value: 'vi', label: 'Vietnamese'},
    {value: 'tr', label: 'Turkish'}, 
    {value: 'it', label: 'Italian'}, 
    {value: 'de', label: 'German'},
    {value: 'fr', label: 'French'}, 
    {value: 'ru', label: 'Russian'}, 
    {value: 'es-es', label: 'Spanish (Spain)'},
    {value: 'es-la', label: 'Spanish (Latin America)'}, 
    {value: 'pt-pt', label: 'Portuguese'},
    {value: 'pt-br', label: 'Portuguese (Brazil)'}
];

// Model presets
export const modelPresets = {
    creative: { temperature: 0.8, top_p: 0.9, presence_penalty: 0.1, frequency_penalty: 0.1 },
    balanced: { temperature: 0.5, top_p: 0.85, presence_penalty: 0.2, frequency_penalty: 0.3 },
    precise: { temperature: 0.2, top_p: 0.75, presence_penalty: 0.5, frequency_penalty: 0.5 }
};

/**
 * Get default properties for a module type
 * Uses the enhanced template system from apps/prompts/
 */
export const getDefaultProperties = async (type) => {
    const baseProperties = {
        fileInput: {
            displayName: 'File Input',
            gameType: '',
            story: '',
            sourceLang: '',
            targetLangs: [],
            sourceColumn: '',
            fileMode: 'translation', // 'translation' or 'calibration'
            targetColumnMappings: {}, // For calibration mode: { langCode: columnName }
            file: null,
            availableColumns: []
        },
        termBase: {
            displayName: 'Term Base',
            cloudFile: null,
            sourceColumn: '',
            targetColumns: []
        },
        memoryBase: {
            displayName: 'Memory Base',
            cloudFile: null,
            sourceColumn: '',
            targetColumns: []
        },
        translation: {
            displayName: 'Translation',
            model: 'gpt-5.4-mini',
            temperature: 0.5,
            top_p: 0.85,
            presence_penalty: 0.0,
            frequency_penalty: 0.0,
            prompt: ''
        },
        review: {
            displayName: 'Review',
            model: 'gpt-5.4',
            temperature: 0.5,
            top_p: 0.85,
            presence_penalty: 0.0,
            frequency_penalty: 0.0,
            prompt: ''
        },
        summary: {
            displayName: 'Summary',
            model: 'gpt-5.4',
            temperature: 0.5,
            top_p: 0.85,
            presence_penalty: 0.0,
            frequency_penalty: 0.0,
            prompt: ''
        }
    };

    const properties = baseProperties[type] || {};

    // For modules that use prompts, load default template from enhanced prompts system
    if (['translation', 'review', 'summary'].includes(type)) {
        try {
            const defaultTemplate = await promptsAPI.getDefaultTemplate(type);
            if (defaultTemplate.success && defaultTemplate.template) {
                properties.prompt = defaultTemplate.template.prompt_text || properties.prompt;
            }
        } catch (error) {
            console.warn(`Failed to load default template for ${type}:`, error);
            // Keep the default empty prompt if loading fails
        }
    }

    return properties;
};

/**
 * Convert file to base64 for API transmission
 */
export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            const base64 = result?.toString().split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Workflow execution service
 * Connects to apps/workflows/tasks/workflow_task_manager.py
 */
export class WorkflowExecutionService {
    constructor() {
        this.pollingInterval = null;
        this.onStatusUpdate = null;
    }

    /**
     * Execute workflow
     * @param {Object} workflowData - Workflow configuration
     * @param {boolean} testMode - Whether to run in test mode
     * @param {number} selectedRow - Row index for test mode
     * @returns {Promise<Object>} Execution result
     */
    async executeWorkflow(workflowData, testMode = false, selectedRow = null) {
        try {
            // Optional safety check: user cost vs. limit
            try {
                const profile = await userAPI.getUserProfile();
                if (profile && profile.success && profile.data && profile.data.user_info) {
                    const ui = profile.data.user_info;
                    const current = Number(ui.current_cost || 0);
                    const limit = Number(ui.cost_limit || 0);
                    if (limit > 0 && current >= limit) {
                        const err = new Error('Cost limit exceeded');
                        err.code = 'COST_LIMIT_EXCEEDED';
                        throw err;
                    }
                }
            } catch (preCheckErr) {
                // If this was a real limit exceed, rethrow; otherwise ignore fetch errors
                if (preCheckErr && preCheckErr.code === 'COST_LIMIT_EXCEEDED') throw preCheckErr;
            }

            // Prepare request payload
            const payload = {
                modules: workflowData.modules,
                properties: workflowData.properties,
                test_mode: testMode
            };

            // Standardize test mode params to backend expectations
            if (testMode && selectedRow !== null) {
                payload.test_row = selectedRow;
            }

            // Include input file hints if present on fileInput module
            try {
                const fileInput = (workflowData.modules || []).find(m => m.type === 'fileInput');
                if (fileInput) {
                    const fp = (workflowData.properties || {})[fileInput.id] || {};
                    // Support both fileId and id from cloudFile
                    if (fp.cloudFile && (fp.cloudFile.fileId || fp.cloudFile.id)) {
                        const cloudId = fp.cloudFile.fileId || fp.cloudFile.id;
                        payload.input_files = {
                            cloud: [{ id: cloudId, name: fp.cloudFile.name }]
                        };
                    }
                    // If local file provided, convert to base64 and attach
                    if (fp.file) {
                        const base64 = await fileToBase64(fp.file);
                        payload.input_files = payload.input_files || {};
                        payload.input_files.fileInput = base64;
                    }
                }
            } catch (_) {
                // noop
            }

            // Execute via enhanced workflow system
            const result = await workflowAPI.executeWorkflow(payload);

            // Backend returns { success: true, data: { job_id, message, ... } }
            const responseData = result && result.data ? result.data : result;

            if (result.success && responseData && responseData.job_id) {
                return {
                    success: true,
                    jobId: responseData.job_id,
                    message: responseData.message,
                    requestId: result._requestId
                };
            } else {
                throw new Error(result.error?.message || 'Workflow execution failed');
            }
        } catch (error) {
            console.error('Workflow execution error:', error);
            throw {
                message: error.message || 'Failed to execute workflow',
                code: error.code,
                requestId: error.requestId,
                status: error.status
            };
        }
    }

    /**
     * Start polling for workflow status
     * @param {string} jobId - Job ID to poll
     * @param {Function} onUpdate - Callback for status updates
     */
    startPolling(jobId, onUpdate) {
        this.onStatusUpdate = onUpdate;

        // Immediately fetch status once to avoid delay
        this.checkStatus(jobId).catch(error => {
            console.error('Initial status check error:', error);
        });

        // Then set up interval for subsequent polling
        this.pollingInterval = setInterval(async () => {
            try {
                await this.checkStatus(jobId);
            } catch (error) {
                console.error('Status polling error:', error);
                if (this.onStatusUpdate) {
                    this.onStatusUpdate({
                        status: 'FAILED',
                        error: error.message || 'Status check failed',
                        requestId: error.requestId
                    });
                }
                this.stopPolling();
            }
        }, 2000); // Poll every 2 seconds
    }

    /**
     * Check workflow status
     * @param {string} jobId - Job ID to check
     */
    async checkStatus(jobId) {
        try {
            const result = await workflowAPI.getWorkflowStatus(jobId);
            // Handle nested data structure from backend: { success, data: {...} }
            const statusData = result && result.data ? result.data : result;
            
            if (this.onStatusUpdate) {
                // Build a richer result object so UI can show summaries without extra fetch
                let mergedResult = null;
                if (statusData.result_files || statusData.execution_summary) {
                    mergedResult = {};
                    if (statusData.result_files) mergedResult.result_files = statusData.result_files;
                    if (statusData.execution_summary) mergedResult.execution_summary = statusData.execution_summary;
                }

                // Ensure UI always gets a truthy result object on SUCCESS to enable buttons
                if (!mergedResult && statusData.status === 'SUCCESS') {
                    mergedResult = { job_id: statusData.job_id };
                }

                this.onStatusUpdate({
                    status: statusData.status,
                    progress: statusData.progress || 0,
                    description: statusData.message || statusData.description || '',
                    // Backend returns error as { code, message, details }; UI renders it as text,
                    // and rendering the raw object crashes React
                    error: typeof statusData.error === 'string'
                        ? statusData.error
                        : statusData.error?.message,
                    result: mergedResult || statusData.result,
                    requestId: result._requestId
                });
            }

            // Stop polling if job is complete
            if (statusData.status === 'SUCCESS' || statusData.status === 'FAILED') {
                this.stopPolling();
                // On success, attempt to fetch presigned download URLs
                if (statusData.status === 'SUCCESS' && statusData.job_id) {
                    try {
                        const res = await workflowAPI.getWorkflowResults(statusData.job_id);
                        const data = res && res.data ? res.data : res;
                        if (this.onStatusUpdate) {
                            this.onStatusUpdate({
                                status: statusData.status,
                                progress: statusData.progress || 100,
                                description: 'Workflow completed successfully',
                                error: null,
                                result: {
                                    result_files: statusData.result_files || {},
                                    download_urls: (data && data.download_urls) || {}
                                },
                                requestId: res._requestId
                            });
                        }
                    } catch (e) {
                        console.warn('Failed to fetch workflow results URLs:', e);
                    }
                }
            }
        } catch (error) {
            console.error('Status check error:', error);
            throw error;
        }
    }

    /**
     * Stop status polling
     */
    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.onStatusUpdate = null;
    }
}

/**
 * Validate workflow configuration
 * @param {Array} modules - Workflow modules
 * @param {Object} properties - Module properties
 * @returns {Object} Validation result
 */
export const validateWorkflow = (modules, properties) => {
    const errors = [];
    const warnings = [];

    // Check for required file input module
    const fileInputModule = modules.find(m => m.type === 'fileInput');
    if (!fileInputModule) {
        errors.push('Workflow must include a File Input module');
    } else {
        const fileProps = properties[fileInputModule.id];
        if (!fileProps?.file && !fileProps?.cloudFile) {
            errors.push('File Input module must have a file selected');
        }
        if (!fileProps?.sourceLang) {
            errors.push('File Input module must have a source language');
        }
        if (!fileProps?.targetLangs?.length) {
            errors.push('File Input module must have at least one target language');
        }
    }

    // Check for at least one processing module
    const processingModules = modules.filter(m => 
        ['translation', 'review', 'summary'].includes(m.type)
    );
    if (processingModules.length === 0) {
        warnings.push('Workflow should include at least one processing module (Translation, Review, or Summary)');
    }

    // Validate module connections (basic check)
    const moduleIds = modules.map(m => m.id);
    modules.forEach(module => {
        if (module.inputs) {
            module.inputs.forEach(inputId => {
                if (!moduleIds.includes(inputId)) {
                    errors.push(`Module ${module.displayName} has invalid input connection`);
                }
            });
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};
